/* The custom builders' map step, for the Custom CUBE and the Custom FRAME (one file since 2026-10-25; moved out of their
   sections 2026-10-24): the section says which builder it is in window.CF.BUILDER_KIND. */
(() => {
  const $  = (sel, root=document) => root.querySelector(sel);
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  const BREAKPOINT = 700;
  const isDesktop = () => matchMedia(`(min-width:${BREAKPOINT}px)`).matches;
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  /*
    What differs between the two builders. A CUBE's model is 0.15 m of the scale on a side, inside a 0.01 m rim; a
    FRAME's is 0.60 m inside 0.02 m, so its map gets less side padding and starts one zoom step further out.
  */
  const IS_FRAME = window.CF.BUILDER_KIND === 'frame';
  const PRODUCT = IS_FRAME ? 'FRAME' : 'CUBE';
  const SIDE_M = IS_FRAME ? 0.60 : 0.15;
  const RIM_M = IS_FRAME ? 0.02 : 0.01;
  /*
    Show the large-scale warning pill when the map scale exceeds this. Beyond the
    usual ~1:3500–1:5500, buildings render small and hard to recognize. (Only the CUBE has the pill.)
  */
  const LARGE_SCALE_WARN_ABOVE = window.CF.BUILDER_T["setting.large_scale_warning_above"];
  const dom = {
    html: document.documentElement,
    galleryCustomEl: $('.cc-custom-stage'),
    scaleDisplay: $('#cube-custom-map-scale-value'),
    scaleWarning: $('#cc-scale-warning'),
    decBtn:       $('#cube-custom-decrease-scale'),
    incBtn:       $('#cube-custom-increase-scale'),
    lockBtn:      $('#cube-custom-lock-frame'),
    rotMinus15:   $('#cube-custom-rot-minus-15'),
    rotMinus1:    $('#cube-custom-rot-minus-1'),
    rotPlus1:     $('#cube-custom-rot-plus-1'),
    rotPlus15:    $('#cube-custom-rot-plus-15'),
    rotationDisplay: $('#cube-custom-rotation-display'),
    locationPlace: $('#cube-custom-location-place'),
    mapEl:        $('#cube-custom-map'),
  };

  if (!dom.galleryCustomEl || !dom.mapEl) return;

  const state = {
    rotation: 0,
    map: null,
    currentScale: 4000,
    isFittingFromSlider: false,
    isFrameLocked: false,
    lockedCenter: null,
    _commitRAF: null,
    _commitPending: false,
    _commitFit: false,
    _commitRotate: false,
  };

  let targetCenter = null;
  let scaleSettleTimer = null;
  let scaleRotationWired = false;
  let reverseGeocodeTimer = null;
  let reverseGeocodeController = null;
  let reverseGeocodeKey = '';
  const FRAME_SOURCE_ID = 'cube-custom-frame-src';
  const FRAME_LAYER_ID  = 'cube-custom-frame-fill';
  const FRAME_LINE_ID   = 'cube-custom-frame-line';
  /*
    Faint "previous design" frame: after a design is made and the user returns to
    the map, we show a low-opacity copy of the frame pinned at that design's exact
    location/scale/rotation, so small area shifts are easy to line up. Same black
    style as the active frame, just weak opacity so it doesn't disturb.
  */
  const PREV_FRAME_SOURCE_ID = 'cube-custom-prevframe-src';
  const PREV_FRAME_FILL_ID   = 'cube-custom-prevframe-fill';
  const PREV_FRAME_LINE_ID   = 'cube-custom-prevframe-line';
  let prevDesignLoc = null; /* { lat, lon, scale, rotation } of the last made design */

  const PADDING_DESKTOP = IS_FRAME ? { top: 150, right: 70, bottom: 150, left: 70 } : { top: 150, right: 200, bottom: 150, left: 200 };
  const PADDING_MOBILE  = IS_FRAME ? { top: 0, right: 40, bottom: 100, left: 40 } : { top: 0, right: 110, bottom: 100, left: 110 };
  const getFramePadding = () => isDesktop() ? PADDING_DESKTOP : PADDING_MOBILE;

  const DEFAULT_FRAME_STYLE = { fill: '#1c1c1c', line: '#000000' };

  function toggleMapControls(disabled) {
    [dom.decBtn, dom.incBtn, dom.rotMinus15, dom.rotMinus1, dom.rotPlus1, dom.rotPlus15]
      .forEach(btn => { if (btn) btn.disabled = disabled; });
  }

  function setMapLoading(isLoading) {
    dom.galleryCustomEl?.classList.toggle('is-loading', !!isLoading);
  }

  function syncTouchRotationInteraction(map) {
    if (!map?.touchZoomRotate) return;
    if (isDesktop()) {
      if (typeof map.touchZoomRotate.disableRotation === 'function') {
        map.touchZoomRotate.disableRotation();
      }
    } else if (typeof map.touchZoomRotate.enableRotation === 'function') {
      map.touchZoomRotate.enableRotation();
    } else if (typeof map.touchZoomRotate.enable === 'function') {
      map.touchZoomRotate.enable();
    }
  }

  function formatRotation(deg) {
    return Math.round(normalizeBearing(deg) * 10) / 10;
  }

  function updateLocationSummary() {
    updateScaleCaption(state.currentScale || 4000);
  }

  function updateScaleCaption(scaleN) {
    const sizeEl = document.getElementById('cf-v2-frame-size');
    const refEl  = document.getElementById('cf-v2-frame-ref');
    if (!sizeEl || !refEl) return;
    const widthM = Math.round(SIDE_M * scaleN);
    sizeEl.textContent = `${widthM} × ${widthM} m`;
    refEl.textContent = scaleReference(widthM);
  }

  function scaleReference(widthM) {
    const r = (window.CF && window.CF.T && window.CF.T.preview && window.CF.T.preview.scale_refs) || {};
    if (widthM <= 180)  return r.pitch        || 'about the size of a soccer pitch';
    if (widthM <= 300)  return r.block        || 'about the size of a city block';
    if (widthM <= 500)  return r.blocks       || 'a few city blocks';
    if (widthM <= 800)  return r.neighborhood || 'a small neighborhood';
    if (widthM <= 1200) return r.center       || 'roughly a city center';
    if (widthM <= 1800) return r.park         || 'about the size of Central Park';
    if (widthM <= 2500) return r.district     || 'a large district';
    if (widthM <= 4000) return r.town         || 'a small town';
    return r.metro || 'a wide metropolitan area';
  }
  /*
    Countries with NATIONWIDE LoD2 buildings (real 3D shapes) → no warning (tier 0).
    PL = GUGiK national LoD2; a few major cities are LoD1-only (→ Overture) and are
    carved back to tier 1 via LOD2_GAP_BBOXES below.
  */
  const LOD2_COUNTRIES = ['DE', 'CH', 'NL', 'LU', 'PL'];
  /*
    Countries with good high-res TERRAIN but only generic (Overture) buildings,
    except inside the LoD2 city/region boxes below → "lighter building detail" (tier 1).
    NOTE: DK has substantial but uneven LoD2 (reseed pending) — kept here (terrain-only)
    on purpose rather than promised country-wide. CZ added (national 2 m terrain; only
    Prague has LoD2, see LOD2_BBOXES).
  */
  const GOOD_TERRAIN_COUNTRIES = ['US', 'GB', 'FR', 'IT', 'AT', 'CA', 'ES', 'PT', 'DK', 'AU', 'BE', 'CZ'];
  /*
    LoD2 building coverage that is REGIONAL / CITY-level, not country-wide. Inside
    one of these the user gets terrain + real buildings (tier 0); the rest of the
    same country falls to tier 1 (good terrain, generic buildings). Boxes are
    w,s,e,n (lon/lat), kept tight to each dataset's actual extent.
  */
  const LOD2_BBOXES = [
    /*  United States — city LoD2 (rest of US = good terrain only) */
    { name: 'us-nyc',      w: -74.30, s: 40.47, e: -73.67, n: 40.95 },
    { name: 'us-boston',   w: -71.20, s: 42.22, e: -70.92, n: 42.40 },
    /*  Belgium — 4 cities (rest of BE = good terrain only) */
    { name: 'be-brussels', w: 4.20,  s: 50.74, e: 4.50,  n: 50.93 },
    { name: 'be-antwerp',  w: 4.20,  s: 51.10, e: 4.55,  n: 51.42 },
    { name: 'be-ghent',    w: 3.58,  s: 50.95, e: 3.88,  n: 51.16 },
    { name: 'be-namur',    w: 4.70,  s: 50.37, e: 5.02,  n: 50.55 },
    /*  Austria — Vienna + Linz (rest of AT = good terrain only) */
    { name: 'at-wien',     w: 16.17, s: 48.10, e: 16.60, n: 48.33 },
    { name: 'at-linz',     w: 14.18, s: 48.20, e: 14.42, n: 48.40 },
    /*  Czech Republic — Prague (rest of CZ = good terrain only) */
    { name: 'cz-prague',   w: 14.10, s: 49.90, e: 14.80, n: 50.20 },
    /*
      France — per-metro LoD2 (downloaded municipal + Roofer LiDAR-HD for Toulouse);
      rest of FR = good terrain only. Boxes are the actual seeded coverage extents.
      NOTE: the CRAIG dataset is scattered intercommunalités — only its dense
      Clermont-Ferrand cluster is boxed; CRAIG's sparse Isère/Auvergne tiles (and
      Grenoble, which CRAIG excludes) are intentionally left as terrain-only.
    */
    { name: 'fr-bordeaux',    w: -0.89, s: 44.74, e: -0.44, n: 45.04 },
    { name: 'fr-lyon',        w: 4.69,  s: 45.55, e: 5.06,  n: 45.94 },
    { name: 'fr-rennes',      w: -1.95, s: 47.92, e: -1.48, n: 48.33 },
    { name: 'fr-strasbourg',  w: 7.54,  s: 48.44, e: 7.88,  n: 48.71 },
    { name: 'fr-montpellier', w: 3.67,  s: 43.50, e: 4.06,  n: 43.77 },
    { name: 'fr-clermont',    w: 3.03,  s: 45.72, e: 3.23,  n: 45.94 },
    { name: 'fr-toulouse',    w: 1.41,  s: 43.58, e: 1.48,  n: 43.63 },
    /*
      Commercial supplier districts — accurate footprints + height but
      FLAT-EXTRUDE (no 3D roofs) and on coarse 30 m terrain. Treated as
      no-warning (tier 0) by product decision; boxes are the seeded extents.
    */
    { name: 'ae-dubai-downtown', w: 55.238, s: 25.145, e: 55.314, n: 25.248 },
    { name: 'ae-dubai-palm',     w: 55.101, s: 25.044, e: 55.172, n: 25.142 },
    { name: 'sa-riyadh-kafd',    w: 46.628, s: 24.748, e: 46.653, n: 24.783 },
    { name: 'sa-riyadh-south',   w: 46.665, s: 24.683, e: 46.693, n: 24.722 },
  ];
  /*
    LoD2 holes inside an otherwise-nationwide-LoD2 country → drop to tier 1
    (good terrain, generic buildings). Boxes are w,s,e,n (lon/lat).
  */
  const LOD2_GAP_BBOXES = [
    { name: 'lu-vdl',     w: 6.076, s: 49.553, e: 6.177, n: 49.650 },
    /*  Poland — major cities served as LoD1 only (fall back to Overture). */
    { name: 'pl-wroclaw', w: 16.85, s: 51.00, e: 17.18, n: 51.21 },
    { name: 'pl-poznan',  w: 16.73, s: 52.32, e: 17.10, n: 52.51 },
    { name: 'pl-gdansk',  w: 18.50, s: 54.28, e: 18.80, n: 54.45 },
    { name: 'pl-lodz',    w: 19.30, s: 51.68, e: 19.62, n: 51.85 },
  ];

  let isFlying = false;

  function normalizeCountryCode(code) {
    return (code || '').split('-')[0].slice(0, 2).toUpperCase();
  }

  function inLod2Bbox(lat, lon) {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
    return LOD2_BBOXES.some(b => lon >= b.w && lon <= b.e && lat >= b.s && lat <= b.n);
  }

  function inLod2Gap(lat, lon) {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
    return LOD2_GAP_BBOXES.some(b => lon >= b.w && lon <= b.e && lat >= b.s && lat <= b.n);
  }

  function coverageTier(cc, lat, lon) {
    if (!cc) return 0; /* unknown (e.g. ocean) → no warning */
    if (inLod2Gap(lat, lon)) return 1; /* LoD2 hole with good terrain (Ville de Luxembourg) */
    if (LOD2_COUNTRIES.includes(cc) || inLod2Bbox(lat, lon)) return 0;
    return GOOD_TERRAIN_COUNTRIES.includes(cc) ? 1 : 2;
  }

  function updateCoverageWarning(countryCode, lat, lon) {
    const el = document.getElementById('cube-custom-coverage-warning');
    if (!el) return;
    if (isFlying) { el.classList.remove('is-visible'); return; }
    const tier = coverageTier(normalizeCountryCode(countryCode), lat, lon);
    el.classList.toggle('is-visible', tier > 0);
    el.querySelectorAll('.cc-coverage-warning__variant').forEach((v) => {
      v.hidden = Number(v.dataset.variant) !== tier;
    });
  }

  document.addEventListener('cf:cube:map-flystart', () => {
    isFlying = true;
    const el = document.getElementById('cube-custom-coverage-warning');
    if (el) el.classList.remove('is-visible');
  });

  function setLocationContext({ city = '', country = '', countryCode = '', area = '', lat = null, lon = null } = {}) {
    state.city = city; state.country = country; state.countryCode = countryCode; state.area = area;
    if (dom.locationPlace) {
      const parts = [city, country].filter(Boolean);
      dom.locationPlace.textContent = parts.length ? parts.join(', ') : 'Choose your area';
    }
    const titleEl = document.getElementById('cf-v2-title');
    if (titleEl) {
      const place = city || country || '';
      const tc = (window.CF && window.CF.T && window.CF.T.cube_custom) || {};
      titleEl.textContent = place
        ? (tc.title_place ? tc.title_place.replace('%place%', place) : `Your ${place} ${PRODUCT}`)
        : (tc.title_default || `Your Custom ${PRODUCT}`);
    }
    updateCoverageWarning(countryCode, lat, lon);
  }

  function readableMapboxName(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(readableMapboxName).find(Boolean) || '';
    }
    return value.name_preferred || value.name || value.text || value.full_address || value.place_name || value.place_formatted || '';
  }

  function contextMatches(value, key) {
    if (!value || typeof value !== 'object') return false;
    const id = String(value.id || value.mapbox_id || '');
    return value.feature_type === key || id.startsWith(`${key}.`) || id.includes(`.${key}.`);
  }

  function readContextName(context, keys) {
    const keyList = Array.isArray(keys) ? keys : [keys];
    if (!context) return '';

    if (Array.isArray(context)) {
      for (const key of keyList) {
        const match = context.find((item) => contextMatches(item, key));
        const name = readableMapboxName(match);
        if (name) return name;
      }
      return '';
    }

    for (const key of keyList) {
      const name = readableMapboxName(context[key]);
      if (name) return name;
    }

    const values = Object.values(context);
    for (const key of keyList) {
      const match = values.find((item) => contextMatches(item, key));
      const name = readableMapboxName(match);
      if (name) return name;
    }

    return '';
  }

  function readCountryCode(data) {
    const features = Array.isArray(data?.features) ? data.features : [];
    for (const f of features) {
      const props = f?.properties || {};
      const ftype = props.feature_type || f?.place_type?.[0];
      if (ftype === 'country') {
        const sc = props.short_code || props.iso_3166_1_alpha_2;
        if (sc) return sc;
      }
      const ctx = props.context || f?.context || [];
      if (Array.isArray(ctx)) {
        for (const c of ctx) {
          const cid = String(c?.id || c?.mapbox_id || '');
          const isCountry = c?.feature_type === 'country' || cid.startsWith('country.') || cid.includes('.country.');
          const sc = c?.short_code || c?.iso_3166_1_alpha_2;
          if (isCountry && sc) return sc;
        }
      } else if (ctx && typeof ctx === 'object' && ctx.country) {
        const sc = ctx.country.short_code || ctx.country.country_code || ctx.country.iso_3166_1_alpha_2;
        if (sc) return sc;
      }
    }
    return '';
  }

  function parseReverseGeocode(data) {
    const features = Array.isArray(data?.features) ? data.features : [];
    const firstProps = features[0]?.properties || {};
    const context = firstProps.context || features[0]?.context || [];
    const featureByType = (types) => {
      const typeList = Array.isArray(types) ? types : [types];
      const feature = features.find((item) => typeList.includes(item?.properties?.feature_type || item?.place_type?.[0]));
      const props = feature?.properties || {};
      return readableMapboxName(props) || readableMapboxName(feature);
    };

    return {
      city: readContextName(context, ['place', 'locality', 'district']) || featureByType(['place', 'locality', 'district']),
      country: readContextName(context, ['country']) || featureByType('country'),
      countryCode: readCountryCode(data),
      area: readContextName(context, ['neighborhood', 'locality'])
            || featureByType(['neighborhood', 'locality']) || '',
    };
  }

  async function updateLocationContext(center) {
    if (!center || !mapboxgl.accessToken) return;

    const key = `${center.lat.toFixed(4)},${center.lng.toFixed(4)}`;
    if (key === reverseGeocodeKey) return;
    reverseGeocodeKey = key;

    if (reverseGeocodeController) reverseGeocodeController.abort();
    reverseGeocodeController = new AbortController();

    try {
      const url = new URL(`https://api.mapbox.com/geocoding/v5/mapbox.places/${center.lng},${center.lat}.json`);
      url.searchParams.set('access_token', mapboxgl.accessToken);
      url.searchParams.set('types', 'neighborhood,locality,place,district,country,address');

      const res = await fetch(url.toString(), { signal: reverseGeocodeController.signal });
      if (!res.ok) throw new Error(`Reverse geocoding failed: ${res.status}`);
      setLocationContext({ ...parseReverseGeocode(await res.json()), lat: center.lat, lon: center.lng });
    } catch (err) {
      if (err.name !== 'AbortError') setLocationContext();
    }
  }

  function scheduleLocationContextUpdate(delay = 350) {
    if (reverseGeocodeTimer) clearTimeout(reverseGeocodeTimer);
    reverseGeocodeTimer = setTimeout(() => {
      const center = state.lockedCenter || (state.map ? state.map.getCenter() : null);
      updateLocationContext(center);
    }, delay);
  }

  function syncRotationFromMap(map) {
    if (!map) return;
    state.rotation = normalizeBearing(map.getBearing() || 0);
    if (dom.rotationDisplay) dom.rotationDisplay.textContent = `${formatRotation(state.rotation)}°`;
    updateLocationSummary();
    updateFrame(map);
  }

  function scaleToDimsMeters(N) {
    const innerW = SIDE_M * N;
    const innerH = SIDE_M * N;
    const t = RIM_M * N;
    return { innerW, innerH, outerW: innerW + 2 * t, outerH: innerH + 2 * t };
  }

  function rectRingFromCenter(centerLngLat, halfWm, halfHm) {
    const c = [centerLngLat.lng, centerLngLat.lat];
    const n  = turf.destination(c, halfHm,   0, { units: 'meters' }).geometry.coordinates;
    const s  = turf.destination(c, halfHm, 180, { units: 'meters' }).geometry.coordinates;
    const ne = turf.destination(n, halfWm,  90, { units: 'meters' }).geometry.coordinates;
    const nw = turf.destination(n, halfWm, 270, { units: 'meters' }).geometry.coordinates;
    const se = turf.destination(s, halfWm,  90, { units: 'meters' }).geometry.coordinates;
    const sw = turf.destination(s, halfWm, 270, { units: 'meters' }).geometry.coordinates;
    return [nw, ne, se, sw, nw];
  }

  function ensureFrameLayers(map) {
    if (map.getSource(FRAME_SOURCE_ID)) return;
    /*  Previous-design frame FIRST so it renders UNDER the active frame. */
    map.addSource(PREV_FRAME_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    map.addLayer({ id: PREV_FRAME_FILL_ID, type: 'fill', slot: 'top', source: PREV_FRAME_SOURCE_ID, paint: { 'fill-color': DEFAULT_FRAME_STYLE.fill, 'fill-opacity': 0.4 } });
    /*  Border matches the active frame exactly (same color + full opacity). */
    map.addLayer({ id: PREV_FRAME_LINE_ID, type: 'line', slot: 'top', source: PREV_FRAME_SOURCE_ID, paint: { 'line-color': DEFAULT_FRAME_STYLE.line, 'line-width': 1, 'line-opacity': 1 } });
    map.addSource(FRAME_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    map.addLayer({ id: FRAME_LAYER_ID, type: 'fill', slot: 'top', source: FRAME_SOURCE_ID, paint: { 'fill-color': DEFAULT_FRAME_STYLE.fill, 'fill-opacity': 1 } });
    map.addLayer({ id: FRAME_LINE_ID,  type: 'line', slot: 'top', source: FRAME_SOURCE_ID, paint: { 'line-color': DEFAULT_FRAME_STYLE.line, 'line-width': 1, 'line-opacity': 1 } });
    refreshPreviousFrame(map);
  }
  /*
    Draw (or clear) the faint previous-design frame from prevDesignLoc, using that
    design's own scale + rotation so it marks its exact footprint.
  */
  function refreshPreviousFrame(map) {
    map = map || state.map;
    if (!map || !map.getSource(PREV_FRAME_SOURCE_ID)) return;
    const loc = prevDesignLoc;
    const la = loc ? parseFloat(loc.lat) : NaN;
    const lo = loc ? parseFloat(loc.lon) : NaN;
    if (!Number.isFinite(la) || !Number.isFinite(lo)) {
      map.getSource(PREV_FRAME_SOURCE_ID).setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const sc = parseFloat(loc.scale);
    const scaleN  = (Number.isFinite(sc) && sc >= 500) ? sc : (state.currentScale || 4000);
    const rot = parseFloat(loc.rotation);
    const bearing = Number.isFinite(rot) ? normalizeBearing(rot) : 0;
    const feature = buildFrameFeatureAt({ lng: lo, lat: la }, scaleN, bearing);
    map.getSource(PREV_FRAME_SOURCE_ID).setData({ type: 'FeatureCollection', features: [feature] });
  }

  function buildFramePolyAt(centerLngLat, scaleN) {
    const { innerW, innerH, outerW, outerH } = scaleToDimsMeters(scaleN);
    const outerRing = rectRingFromCenter(centerLngLat, outerW / 2, outerH / 2);
    const innerRing = rectRingFromCenter(centerLngLat, innerW / 2, innerH / 2).slice().reverse();
    return turf.polygon([outerRing, innerRing]);
  }

  function buildFrameFeatureAt(centerLngLat, scaleN, bearingDeg) {
    const pivot = [centerLngLat.lng, centerLngLat.lat];
    const basePoly = buildFramePolyAt(centerLngLat, scaleN);
    const rotated = turf.transformRotate(basePoly, bearingDeg || 0, { pivot });
    return { type: 'Feature', properties: { scale: `1:${scaleN}` }, geometry: rotated.geometry };
  }

  function updateFrame(map, overrideCenter = null) {
    if (!map || !map.getSource(FRAME_SOURCE_ID)) return;
    const center  = overrideCenter || state.lockedCenter || map.getCenter();
    const scaleN  = state.currentScale || 1000;
    const bearing = normalizeBearing(state.rotation ?? map.getBearing() ?? 0);
    const feature = buildFrameFeatureAt(center, scaleN, bearing);
    map.getSource(FRAME_SOURCE_ID).setData({ type: 'FeatureCollection', features: [feature] });
  }

  function cameraForScale(map, centerLngLat, scaleN) {
    const basePoly = buildFramePolyAt(centerLngLat, scaleN);
    const b = turf.bbox(basePoly);
    const bounds = [[b[0], b[1]], [b[2], b[3]]];
    return map.cameraForBounds(bounds, { padding: getFramePadding(), bearing: 0, pitch: map.getPitch() || 0 });
  }

  function applyScaleFit(map, scaleN, { animate = true } = {}) {
    if (!map) return false;
    const c = map.getContainer();
    if (!c || !c.clientWidth || !c.clientHeight) return false;
    if (!targetCenter) targetCenter = state.lockedCenter || map.getCenter();
    const cam = cameraForScale(map, targetCenter, scaleN);
    if (!cam || !Number.isFinite(cam.zoom)) return false; /* NaN is typeof 'number' — must use isFinite */
    state.isFittingFromSlider = true;
    const target = { center: targetCenter, zoom: cam.zoom, bearing: normalizeBearing(state.rotation), pitch: map.getPitch() || 0 };
    if (animate) map.easeTo({ ...target, duration: 250, essential: true });
    else map.jumpTo(target);
    updateFrame(map, targetCenter);
    if (scaleSettleTimer) clearTimeout(scaleSettleTimer);
    scaleSettleTimer = setTimeout(() => {
      state.isFittingFromSlider = false;
      updateFrame(map, targetCenter);
      targetCenter = null;
    }, 300);
    return true;
  }

  function scheduleCommit({ fit = false, rotate = false } = {}) {
    state._commitPending = true;
    state._commitFit = state._commitFit || !!fit;
    state._commitRotate = state._commitRotate || !!rotate;
    if (state._commitRAF) return;
    state._commitRAF = requestAnimationFrame(() => {
      state._commitRAF = null;
      if (!state._commitPending) return;
      state._commitPending = false;
      const map = state.map;
      if (!map || !map.isStyleLoaded()) return;
      ensureFrameLayers(map);
      const scaleN  = state.currentScale || 1000;
      const bearing = normalizeBearing(state.rotation);
      if (state._commitRotate) {
        state._commitRotate = false;
        map.stop();
        map.easeTo({ bearing, duration: 120, essential: true });
      }
      updateFrame(map);
      if (state._commitFit) {
        state._commitFit = false;
        applyScaleFit(map, scaleN, { animate: true });
      }
    });
  }

  function initCubeCustomMap() {
    if (state.map) return state.map;
    if (!dom.mapEl) return null;
    mapboxgl.accessToken = 'pk.eyJ1IjoiY2l0eWZyYW1lcyIsImEiOiJjbTNmcGNsbnAwcTFxMmlyM21tMGFiNzRvIn0.NBnCw5-cC2b7y5LnGJ7-kA';
    setMapLoading(true);
    /*
      Default map view by the visitor's localization country. Each country
      opens on its flagship CUBE area — i.e. the largest city we sell a CUBE
      for (matching the existing convention: Zürich over Bern, NYC over DC).
      Coordinates [lng, lat] are taken from that variant's custom.coordinates
      metafield on the `cube` product. Any country not listed falls back to DE.
    */
    const COUNTRY_VIEWS = {
      AE: [55.274358, 25.197097],
      AR: [-58.381625, -34.603722],
      AT: [16.373267, 48.208389],
      AU: [151.210756, -33.852219],
      AZ: [49.826508, 40.359958],
      BE: [4.351917, 50.846628],
      BH: [50.581511, 26.239636],
      BR: [-43.173169, -22.965544],
      CA: [-79.387139, 43.642619],
      CH: [8.5417, 47.3769],
      CL: [-70.606808, -33.417147],
      CN: [121.504183, 31.234808],
      CO: [-74.068061, 4.613097],
      CU: [-82.359531, 23.134961],
      CZ: [14.421183, 50.087711],
      DE: [13.400374902010412, 52.51695710364052],
      DK: [12.589225, 55.680156],
      EE: [24.745242, 59.437314],
      EG: [31.134211, 29.979222],
      ES: [-3.703425, 40.416947],
      FI: [24.952122, 60.170561],
      FR: [2.294536, 48.858272],
      GB: [-0.124553, 51.500725],
      GR: [23.726636, 37.971503],
      HK: [114.161589, 22.279317],
      HR: [18.110286, 42.640850],
      HU: [19.045672, 47.507117],
      ID: [106.823306, -6.210125],
      IE: [-6.260300, 53.349800],
      IL: [34.790342, 32.073344],
      IN: [72.834650, 18.921950],
      IS: [-21.927053, 64.141978],
      IT: [12.492336, 41.890200],
      JP: [139.745519, 35.658433],
      KR: [126.983375, 37.580356],
      KW: [47.992917, 29.378370],
      LB: [35.471600, 33.891300],
      LU: [6.1296, 49.6116],
      MA: [-7.989031, 31.625992],
      MC: [7.427833, 43.739219],
      MO: [113.543372, 22.190853],
      MX: [-99.167664, 19.427031],
      MY: [101.711608, 3.158106],
      NL: [4.8952, 52.3702],
      NO: [10.753231, 59.907644],
      NZ: [174.762219, -36.848500],
      PH: [121.024461, 14.554894],
      PL: [21.006239, 52.231722],
      PT: [-9.136447, 38.707525],
      QA: [51.527769, 25.318772],
      RS: [20.456894, 44.817811],
      RU: [37.617547, 55.751039],
      SA: [46.674325, 24.711483],
      SE: [18.071106, 59.324861],
      SG: [103.860506, 1.283594],
      TH: [100.528361, 13.723428],
      TR: [28.980136, 41.008603],
      TW: [121.564711, 25.033906],
      UA: [30.524517, 50.449836],
      US: [-73.9857, 40.7484], /* New York — Midtown Manhattan (nyc_lod2) */
      ZA: [18.423169, -33.918522],
    };
    const cc = ((window.CF.CONSTANTS && window.CF.CONSTANTS.countryCode) || '').toUpperCase();
    /*
      A saved-design link carries the place: the map starts there (2026-10-26). It used to start at the country's
      view and jump on its load, so it fetched that city's tiles first - Berlin's, for every German visitor - for a
      map nobody saw. A link with the id only still starts at the country's view and is placed when the record lands.
    */
    const linkQuery = new URLSearchParams(location.search);
    const linkLat = parseFloat(linkQuery.get('lat'));
    const linkLon = parseFloat(linkQuery.get('lon'));
    const linkCenter = Number.isFinite(linkLat) && Number.isFinite(linkLon) && Math.abs(linkLat) <= 85 && Math.abs(linkLon) <= 180
      ? [linkLon, linkLat] : null;
    const map = new mapboxgl.Map({
      container: dom.mapEl,
      style: 'mapbox://styles/cityframes/cmpidmwon002m01sgap537480',
      center: linkCenter || COUNTRY_VIEWS[cc] || COUNTRY_VIEWS.DE,
      zoom: IS_FRAME ? 13.6 : 14.6,
      minZoom: 2,
      maxZoom: 17,
      preserveDrawingBuffer: true,
      interactive: true,
      /*
        antialias OFF: a multisampled WebGL buffer reads back blank on many mobile
        GPUs even with preserveDrawingBuffer, so PostHog/rrweb captured an empty
        map in mobile session replays. Plain buffer = readable on mobile. Minor
        edge-smoothing loss on the 3D buildings; preserveDrawingBuffer stays on.
      */
      antialias: false
    });
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      marker: false,
      placeholder: window.CF.T.cube_custom.search_placeholder || 'Search...'
    });
    geocoder.on('result', (event) => {
      document.dispatchEvent(new CustomEvent('cf:cube:map-flystart'));
      if (event?.result) setLocationContext(parseReverseGeocode({ features: [event.result] }));
      (geocoder._inputEl || document.querySelector('.mapboxgl-ctrl-geocoder input'))?.blur();
    });
    geocoder.on('clear', () => scheduleLocationContextUpdate(0));
    map.addControl(geocoder, 'top-left');
    syncTouchRotationInteraction(map);
    map.keyboard.disable();
    map.dragRotate.disable();
    map.touchPitch.disable();
    state.map = map;

    const myLocBtn = document.getElementById('cube-custom-my-location');
    if (myLocBtn && 'geolocation' in navigator) {
      myLocBtn.addEventListener('click', () => {
        myLocBtn.disabled = true;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            myLocBtn.disabled = false;
            document.dispatchEvent(new CustomEvent('cf:cube:map-flystart'));
            map.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 14.6, essential: true });
          },
          () => { myLocBtn.disabled = false; },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      });
    } else if (myLocBtn) {
      myLocBtn.parentElement.style.display = 'none';
    }

    const fsBtn   = document.getElementById('cube-custom-fullscreen');
    const stageEl = dom.galleryCustomEl;
    if (fsBtn && stageEl) {
      const FS_LABEL  = window.CF.BUILDER_T["fullscreen_aria"];
      const FS_EXIT   = window.CF.BUILDER_T["exit_fullscreen_aria"];
      const setFullscreen = (on) => {
        stageEl.classList.toggle('is-fullscreen', on);
        document.body.classList.toggle('cc-map-fs-open', on);
        fsBtn.setAttribute('aria-label', on ? FS_EXIT : FS_LABEL);
        fsBtn.setAttribute('title', on ? FS_EXIT : FS_LABEL);
        requestAnimationFrame(() => {
          map.resize();
          requestAnimationFrame(() => map.resize());
        });
      };
      fsBtn.addEventListener('click', () => {
        setFullscreen(!stageEl.classList.contains('is-fullscreen'));
        fsBtn.blur(); /* drop focus so the tap doesn't leave a stuck hover/active state */
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && stageEl.classList.contains('is-fullscreen')) {
          setFullscreen(false);
        }
      });
      /*
        On-map Generate (fullscreen only): close the map first — generation
        switches the stage to the preview view, which shouldn't stay pinned to
        the fullscreen map — then delegate to the real Generate button so all
        existing validation/run/view-switch logic fires exactly once.
      */
      const fsGenBtn = document.getElementById('cube-custom-fs-generate');
      if (fsGenBtn) {
        fsGenBtn.addEventListener('click', () => {
          setFullscreen(false);
          document.getElementById('cube-custom-get-preview')?.click();
        });
      }
    }

    const rotationMediaQuery = matchMedia(`(min-width:${BREAKPOINT}px)`);
    const onViewportChange = () => syncTouchRotationInteraction(map);
    if (rotationMediaQuery.addEventListener) {
      rotationMediaQuery.addEventListener('change', onViewportChange);
    } else if (rotationMediaQuery.addListener) {
      rotationMediaQuery.addListener(onViewportChange);
    }

    const onMove = () => {
      if (state.isFittingFromSlider) return;
      updateFrame(map);
      updateLocationSummary();
    };

    const hideLoader = () => setMapLoading(false);
    map.on('load', () => {
      state.mapLoaded = true;
      ensureFrameLayers(map);
      map.on('move', onMove);
      map.on('rotate', () => syncRotationFromMap(map));
      map.on('moveend', () => {
        isFlying = false; /* arrived → let the warning settle for the new spot */
        updateLocationSummary();
        scheduleLocationContextUpdate();
        document.dispatchEvent(new CustomEvent('cf:cube:map-moved'));
      });
      map.resize();
      updateFrame(map);
      state._needsInitialFit = !applyScaleFit(map, state.currentScale, { animate: false });
      updateLocationSummary();
      scheduleLocationContextUpdate(0);
      requestAnimationFrame(hideLoader);
      /*
        Redraw the frame once the map is fully sized and idle. At 'load' the
        stage container isn't always at its final size yet, so the initial
        updateFrame/fit can land on a 0-size map and the frame would only
        appear after the first manual move. Doing it on 'idle' fixes that.
      */
      map.once('idle', () => {
        hideLoader();
        ensureFrameLayers(map);
        if (state._needsInitialFit) {
          state._needsInitialFit = !applyScaleFit(map, state.currentScale, { animate: false });
        }
        updateFrame(map);
      });
      document.dispatchEvent(new CustomEvent('cf:cube:map-moved'));
    });
    setTimeout(hideLoader, 6000);
    return map;
  }

  function normalizeBearing(deg) {
    let v = deg % 360;
    if (v < 0) v += 360;
    return v;
  }

  function setMapRotation(deg) {
    const map = state.map;
    if (!map || !map.isStyleLoaded()) return;
    state.rotation = normalizeBearing(deg);
    if (dom.rotationDisplay) dom.rotationDisplay.textContent = `${state.rotation}°`;
    updateLocationSummary();
    scheduleCommit({ rotate: true });
  }

  function setScaleUI(v) {
    if (dom.scaleDisplay) dom.scaleDisplay.textContent = String(v);
    if (dom.scaleWarning) dom.scaleWarning.hidden = !(Number(v) > LARGE_SCALE_WARN_ABOVE);
    updateLocationSummary();
  }

  function commitScale(scaleN, { fit = true } = {}) {
    state.currentScale = scaleN;
    window.CUBE_CUSTOM_SCALE = `1:${scaleN}`;
    setScaleUI(scaleN);
    if (!state.map || !state.map.isStyleLoaded()) return;
    scheduleCommit({ fit });
  }

  function wireScaleAndRotationInputs() {
    if (scaleRotationWired) return;
    const { decBtn, incBtn, lockBtn, rotMinus15, rotMinus1, rotPlus1, rotPlus15 } = dom;

    const setLockLabel = (locked) => {
      if (!lockBtn) return;
      const t = locked ? window.CF.T.cube_custom.unlock_frame
                       : window.CF.T.cube_custom.lock_frame;
      lockBtn.setAttribute('aria-label', t);
      lockBtn.setAttribute('title', t);
    };

    if (lockBtn) {
      on(lockBtn, 'click', () => {
        state.isFrameLocked = !state.isFrameLocked;
        if (state.isFrameLocked) {
          state.lockedCenter = state.map.getCenter();
          lockBtn.classList.add('is-locked');
          setLockLabel(true);
          toggleMapControls(true);
          updateLocationSummary();
          scheduleLocationContextUpdate(0);
        } else {
          state.lockedCenter = null;
          lockBtn.classList.remove('is-locked');
          setLockLabel(false);
          updateFrame(state.map);
          toggleMapControls(false);
          updateLocationSummary();
          scheduleLocationContextUpdate(0);
        }
      });
    }

    const SCALE_MIN = 2500;
    const SCALE_MAX = 7500;
    function getDynamicStep(value, isDescending = false) {
      return 500; /* uniform 1:500 steps across the 1:2500–1:7500 range */
    }

    function applyScaleFromUser(raw, { fit = false } = {}) {
      let v = parseInt(String(raw || ''), 10);
      if (!Number.isFinite(v)) v = state.currentScale || SCALE_MIN;
      let dynamicStep = getDynamicStep(v, raw < state.currentScale);
      v = clamp(Math.round(v / dynamicStep) * dynamicStep, SCALE_MIN, SCALE_MAX);
      commitScale(v, { fit });
    }

    on(decBtn, 'click', () => applyScaleFromUser(state.currentScale - getDynamicStep(state.currentScale, true)));
    on(incBtn, 'click', () => applyScaleFromUser(state.currentScale + getDynamicStep(state.currentScale, false)));

    function applyRotationFromUser(delta) {
      let newRotation = (state.rotation || 0) + delta;
      newRotation = (newRotation % 360 + 360) % 360;
      state.rotation = newRotation;
      if (dom.rotationDisplay) dom.rotationDisplay.textContent = `${newRotation}°`;
      updateLocationSummary();
      setMapRotation(newRotation);
    }

    on(rotMinus15, 'click', () => applyRotationFromUser(-15));
    on(rotMinus1,  'click', () => applyRotationFromUser(-1));
    on(rotPlus1,   'click', () => applyRotationFromUser(1));
    on(rotPlus15,  'click', () => applyRotationFromUser(15));

    scaleRotationWired = true;
  }

  window.CF_CUBE_CUSTOM_MAP = {
    getState: () => ({
      scale: state.currentScale,
      rotation: formatRotation(state.rotation),
      center: state.lockedCenter || (state.map ? state.map.getCenter() : null),
      city: state.city || '',
      country: state.country || '',
      countryCode: (state.countryCode || '').toUpperCase(),
      area: state.area || '',
    }),
    getMap: () => state.map,
    /*  Record the just-made design's location and (re)draw the faint marker frame. */
    setPreviousDesign: (loc) => {
      if (loc && Number.isFinite(parseFloat(loc.lat)) && Number.isFinite(parseFloat(loc.lon))) {
        prevDesignLoc = { lat: loc.lat, lon: loc.lon, scale: loc.scale, rotation: loc.rotation };
      }
      refreshPreviousFrame(state.map);
    },
    /*  Re-draw the faint previous-design frame (call when returning to the map). */
    refreshPreviousFrame: () => refreshPreviousFrame(state.map),
    resize: () => {
      const m = state.map;
      if (!m) return;
      m.resize();
      if (state._needsInitialFit) {
        const c = m.getContainer();
        if (c && c.clientWidth && c.clientHeight) {
          state._needsInitialFit = false;
          applyScaleFit(m, state.currentScale, { animate: false });
        }
      }
    },
    flyTo: (lat, lon) => {
      if (!state.map || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
      if (state.isFrameLocked) {
        state.isFrameLocked = false;
        state.lockedCenter = null;
        const lockBtn = document.getElementById('cube-custom-lock-frame');
        if (lockBtn) {
          lockBtn.classList.remove('is-locked');
          const t = window.CF.T.cube_custom.lock_frame;
          lockBtn.setAttribute('aria-label', t);
          lockBtn.setAttribute('title', t);
        }
        toggleMapControls(false);
      }
      targetCenter = { lng: lon, lat };
      state.map.flyTo({ center: [lon, lat], zoom: 14.6, duration: 1200, essential: true });
      state.map.once('moveend', () => {
        updateFrame(state.map, targetCenter);
        updateLocationSummary();
        scheduleLocationContextUpdate(0);
      });
    },
    applySavedState: ({ lat, lon, scale, rotation, onSettled } = {}) => {
      const map = state.map;
      const la = Number(lat), lo = Number(lon);
      if (!map || !Number.isFinite(la) || !Number.isFinite(lo)) return; /* never feed Mapbox NaN */
      if (state.isFrameLocked) {
        state.isFrameLocked = false;
        state.lockedCenter = null;
        const lockBtn = document.getElementById('cube-custom-lock-frame');
        if (lockBtn) {
          lockBtn.classList.remove('is-locked');
          const t = window.CF.T.cube_custom.lock_frame;
          lockBtn.setAttribute('aria-label', t);
          lockBtn.setAttribute('title', t);
        }
        toggleMapControls(false);
      }
      const sc = Number(scale);
      if (Number.isFinite(sc) && sc > 0) {
        state.currentScale = sc;
        window.CUBE_CUSTOM_SCALE = `1:${sc}`;
        setScaleUI(sc);
      }
      const ro = Number(rotation);
      if (Number.isFinite(ro)) {
        state.rotation = normalizeBearing(ro);
        if (dom.rotationDisplay) dom.rotationDisplay.textContent = `${state.rotation}°`;
      }
      const bearing = Number.isFinite(state.rotation) ? state.rotation : 0;
      const settle = () => {
        const cs = Number(state.currentScale);
        targetCenter = { lng: lo, lat: la };
        if (Number.isFinite(cs) && cs > 0) applyScaleFit(map, cs, { animate: false });
        updateFrame(map, { lng: lo, lat: la });
        updateLocationSummary();
        scheduleLocationContextUpdate(0);
        if (typeof onSettled === 'function') onSettled(); /* map is now at the saved spot */
      };
      targetCenter = { lng: lo, lat: la };
      /* Once the map has loaded, fly there; before, wait for the load. NOT isStyleLoaded(): just after the load it
         can still say false while the terrain source comes in, and waiting for a load that has already fired left a
         saved-design link on the default map (Berlin) - about every second time the id-first reopen came in after
         the load (2026-10-24, measured: load 2,903 ms, applySavedState 3,330 ms with isStyleLoaded() false). */
      if (state.mapLoaded || map.isStyleLoaded()) {
        map.flyTo({ center: [lo, la], bearing, duration: 600, essential: true });
        map.once('moveend', settle);
      } else {
        map.once('load', () => {
          map.jumpTo({ center: [lo, la], bearing });
          settle();
        });
      }
    },
  };

  document.addEventListener('DOMContentLoaded', () => {
    dom.html.classList.add('is-custom-city');
    if (initCubeCustomMap()) {
      wireScaleAndRotationInputs();
    }
  });
})();
